import { bandOf, INDUSTRIES, MISSIONS } from '@cyberscore/contracts';
import { Printer, Target } from 'lucide-react';
import { href } from '../../lib/router';
import { bandLabel } from '../portfolio/format';
import { Delta } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { CardHead, MissionIcon, controlLink } from './parts';
import { NoAssessment, ScoreProblem } from './states';
import { TargetCard } from './TargetCard';

/**
 * The 90-day plan, as the prototype lays it out: today's score, where the plan takes it, and three phases
 * of steps ranked as missions rank them (what holds the score down first). Each phase's points count on
 * top of the phases before it.
 */
export function PlanPage() {
  const { loading } = useBusiness();
  const { current: a, view, derived } = useBusinessScore();
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  if (view.isPending) return <div className="empty">Loading…</div>;
  if (view.isError || !derived) return <ScoreProblem a={a} error={view.error} />;

  const { score: r, explanation: trace } = view.data;
  const { plan, missions } = derived;
  const end = plan.phases[plan.phases.length - 1]?.score ?? r.score;
  const mission = (id: string) => MISSIONS.find((m) => m.id === id);
  const steps = plan.phases.reduce((n, p) => n + p.steps.length, 0);

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Your 90-day plan</h1>
          <p>The steps that add the most to your score, starting with what holds it down. Share it with your IT provider.</p>
        </div>
        <div className="head-actions">
          <button type="button" className="bbtn" onClick={() => window.print()}>
            <Printer size={15} /> Print the plan
          </button>
        </div>
      </div>
      <div className="bgrid g-4">
        <section className="bcard stat">
          <div className="lbl">Today</div>
          <div className="val num">{r.score}</div>
          <div className="foot">{bandLabel(r.band).toLowerCase()} · confidence {Math.round(r.controls.evidence_confidence * 100)}%</div>
        </section>
        <section className="bcard stat">
          <div className="lbl">In 90 days</div>
          <div className="val num">{end} <Delta n={end - r.score} /></div>
          <div className="foot">{bandLabel(bandOf(end)).toLowerCase()} · the {steps} steps below, together</div>
        </section>
        <section className="bcard stat">
          <div className="lbl">Everything done</div>
          <div className="val num">{r.score + plan.all.points} <Delta n={plan.all.points} /></div>
          <div className="foot">All {plan.all.controls} open protections, together</div>
        </section>
        <section className="bcard stat">
          <div className="lbl">Missions open</div>
          <div className="val num">{missions.filter((m) => m.next.steps.length).length}</div>
          <div className="foot">of {missions.length} for your business</div>
        </section>
      </div>

      <TargetCard a={a} trace={trace} score={r.score} />

      {plan.phases.length ? (
        <div className="roadmap mt-l">
          {plan.phases.map((p, i) => (
            <section key={p.name} className="phase">
              <div className="row-between">
                <h4>{p.name}</h4>
                <b className="num ok-t">+{p.points}</b>
              </div>
              <div className="when">{i === 0 ? (p.steps.some((s) => s.lifts_cap) ? 'Lift the cap first' : 'Biggest gains first') : i === 1 ? 'Next most valuable' : 'Rounding out'} · score {p.score} after</div>
              <div className="mt-s">
                {p.steps.map((s) => {
                  const m = mission(s.mission);
                  const link = controlLink(a, trace, s.control, s.mission);
                  return (
                    <a key={s.control} className="task" href={link.to}>
                      <span className="grow">
                        <span className="tname">{s.name}</span>
                        <span className="muted tiny row gap-xs">
                          {m && <><MissionIcon id={m.id} size={12} />{m.name}{m.pack ? ` · ${INDUSTRIES[m.pack]}` : ''}</>}
                          {s.lifts_cap && <span className="bad-t"> · lifts the cap</span>}
                        </span>
                      </span>
                      <span className="num ok-t strong">+{s.points}</span>
                    </a>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <section className="bcard mt-l"><p className="muted">Nothing left that would add points. Keep your evidence current.</p></section>
      )}

      <section className="bcard mt-l">
        <CardHead tone="ok" icon={<Target size={18} />} title="By mission" sub="What finishing each mission adds, from today’s score. Missions don’t add up: each counts on its own." />
        <div className="btbl-wrap">
          <table className="btbl">
            <thead>
              <tr><th>Mission</th><th className="r">In place</th><th className="r">Next steps</th><th className="r">Whole mission</th></tr>
            </thead>
            <tbody>
              {missions.filter((m) => m.next.steps.length).map((m) => (
                <tr key={m.id}>
                  <td><a href={href.missions(m.id)} className="row gap-s"><MissionIcon id={m.id} size={15} /><b>{m.name}</b></a></td>
                  <td className="r num">{m.in_place}/{m.controls}</td>
                  <td className="r num">+{m.next.points}</td>
                  <td className="r num ok-t strong">{m.points ? `+${m.points}` : m.after_cap ? `+${m.after_cap} after the cap` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
} // prettier-ignore
